import { defineMessages } from "react-intl";
import { produce } from "immer";
import { of, merge, concat } from "rxjs";
import { filter, catchError, delay } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { ServerResponseStatus } from "../../api/types.js";
import "moment-timezone";
import "@apollo/client/errors";
import "@apollo/client";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import { mergeMap, map } from "../../otel/rxjs.js";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import { BaseEpicsBuilder } from "../../redux-folder/epicsBuilder.js";
import "../../redux-folder/actions.js";
import { messageActions } from "../../redux-folder/message/actions.js";
import { getViewActions } from "../../redux-folder/view/actions.js";
import "fast-deep-equal/es6";
import "redux";
import "../../redux-folder/entity/epics.js";
import "normalizr";
import "../../redux-folder/entity/EntityProvider.js";
import "../../redux-folder/realtime/RealtimeNotification.js";
import "reselect";
import "react-redux";
import "react-router";
import { bulkActionsActions, PollingState } from "./actions.js";
import { bulkOperationsNotFailedIdsSelector, bulkOperationsFilterSelector, actionsDataWithoutSkippedItemsSelector } from "./selectors.js";
import { BulkItemStatus } from "./types.js";
import { InvalidStructureError } from "../../file/services/BaseExcelDocumentBuilder.js";
import "papaparse";
import "../../file/services/CsvDocumentBuilderAdapter.js";
import "../../file/services/FileDownloadService.js";
import "exceljs";
const localized = defineMessages({
  incorrectType: {
    id: "ActionBulkEpicsBuilder_incorrectType",
    defaultMessage: "Please make sure direction in the file is equal to selected bulk type."
  },
  incorrectStructure: {
    id: "ActionBulkEpicsBuilder_incorrectStructure",
    defaultMessage: "Wrong file structure. Expected column {expectedValue}, recieved {recievedValue}."
  },
  defaultError: {
    id: "ActionBulkEpicsBuilder_defaultError",
    defaultMessage: "Can't parse your file."
  }
});
class ActionBulkEpicsBuilder extends BaseEpicsBuilder {
  _bulkOperationService;
  _viewActions;
  _bulkOperationEntityType;
  constructor(bulkOperationService, bulkOperationEntityType) {
    super();
    this._bulkOperationService = bulkOperationService;
    this._bulkOperationEntityType = bulkOperationEntityType;
    this._viewActions = getViewActions();
  }
  buildEpicList() {
    return [
      this.buildLoadItemsEpic(),
      this.buildValidateEpic(),
      this.buildPerformEpic(),
      this.buildApplyEpic(),
      this.buildRetryEpic(),
      this.buildPollingEpic(),
      this.buildInstantOperationEpic()
    ];
  }
  buildLoadItemsEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.loadItems)),
      mergeMap(
        (action) => action.payload.strategy.process(action.payload.request).pipe(
          map(bulkActionsActions.setBulkActionsItems),
          catchError((err) => {
            let message = void 0;
            if (err instanceof TypeError) {
              message = localized.incorrectType;
            } else if (err instanceof InvalidStructureError) {
              message = localized.incorrectStructure;
            } else {
              message = localized.defaultError;
            }
            return of(
              bulkActionsActions.clearBulkActions(),
              messageActions.saveMessage({
                message,
                values: err.cause ?? {},
                mode: "error"
              })
            );
          })
        )
      )
    );
  }
  buildValidateEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.validate)),
      mergeMap((action) => action.payload.strategy.process(action.payload.request)),
      map(bulkActionsActions.setValidationResults)
    );
  }
  buildApplyEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.apply)),
      map((action) => {
        const { state, items, actionKey, shouldMergeState } = this.applyOperation(action.payload);
        return items?.length ? bulkActionsActions.setBulkActions({ state, shouldMergeState }) : bulkActionsActions.removeBulkActionsWithKey(actionKey);
      })
    );
  }
  buildPerformEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.perform)),
      mergeMap((action) => {
        const performPayloads = produce(action.payload, (draftState) => {
          const performableStatuses = [
            BulkItemStatus.Pending,
            BulkItemStatus.Failed,
            BulkItemStatus.TimedOut
          ];
          return draftState?.filter((payload) => payload?.request?.items?.some((i) => performableStatuses.includes(i?.status))).map((payload) => {
            const performableItems = payload.request.items?.filter((i) => performableStatuses.includes(i?.status))?.map((i) => ({ ...i, status: BulkItemStatus.InProgress }));
            return { ...payload, request: { ...payload.request, items: performableItems } };
          });
        });
        const changeStatusObservables = performPayloads?.map(
          (payload) => of(bulkActionsActions.changeBulkActionStatus(payload.request))
        );
        const performObservables = performPayloads?.map((a) => a.strategy?.process(a.request)?.pipe(map(a?.postProcessingAction)))?.filter((o) => o);
        return merge(...changeStatusObservables, ...performObservables);
      })
    );
  }
  buildInstantOperationEpic() {
    return (action$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.instantOperation)),
      mergeMap((action) => {
        const { applyRequest, applyStrategy, performStrategy } = action.payload;
        const { actionKey, items, state, shouldMergeState } = this.applyOperation({
          request: applyRequest,
          strategy: applyStrategy
        });
        return concat(
          of(bulkActionsActions.setBulkActions({ state, shouldMergeState })),
          of(
            bulkActionsActions.perform([
              {
                request: { actionKey, items },
                strategy: performStrategy,
                postProcessingAction: bulkActionsActions.saveOperationId
              }
            ])
          )
        );
      })
    );
  }
  buildRetryEpic() {
    return (action$, state$) => action$.pipe(
      filter(isActionOf(bulkActionsActions.retry)),
      mergeMap((action) => {
        const operationIdsState = bulkOperationsNotFailedIdsSelector(state$.value);
        const operationIdList = Object.values(operationIdsState);
        return operationIdList.length > 0 ? this.retryOperations(action.payload, state$.value) : of(bulkActionsActions.cleanOperationIds(), bulkActionsActions.perform(action.payload));
      })
    );
  }
  buildPollingEpic() {
    return (action$, state$) => {
      let isPollingEnabled = false;
      return action$.pipe(
        filter(isActionOf(bulkActionsActions.fetchOperations)),
        delay(4e3),
        mergeMap((action) => {
          let result = of();
          if (action.payload.pollingState === PollingState.Start) {
            isPollingEnabled = true;
          } else if (action.payload.pollingState === PollingState.Finished) {
            isPollingEnabled = false;
          }
          if (isPollingEnabled) {
            const filter2 = bulkOperationsFilterSelector(state$.value);
            result = filter2 ? of(
              this._viewActions.updateFilter({
                entity: this._bulkOperationEntityType,
                view: action.payload.viewType,
                filter: `id=${filter2}`
              }),
              bulkActionsActions.fetchOperations({
                viewType: action.payload.viewType,
                pollingState: PollingState.InProgress
              })
            ) : of(
              bulkActionsActions.fetchOperations({
                viewType: action.payload.viewType,
                pollingState: PollingState.InProgress
              })
            );
          }
          return result;
        })
      );
    };
  }
  applyOperation(request) {
    const payload = request.strategy.process(request.request);
    const appliedItems = payload.items?.filter((i) => i.value !== null);
    const shouldMergeState = payload.shouldMergeState;
    const result = appliedItems.reduce(
      (state, item) => (state[item.actionKey] = appliedItems?.filter((i) => i.actionKey === item.actionKey).map((item2) => ({ ...item2, actionKey: payload.actionKey })), state),
      {}
    );
    return { state: result, items: appliedItems, actionKey: payload.actionKey, shouldMergeState };
  }
  retryOperations(payload, state) {
    const operationIdsState = bulkOperationsNotFailedIdsSelector(state);
    const operationIdList = Object.values(operationIdsState);
    return this.getOperations(operationIdList).pipe(
      mergeMap((res) => {
        if (res.status !== ServerResponseStatus.Success) {
          return of();
        }
        const actionItems = actionsDataWithoutSkippedItemsSelector(state);
        const operations = res.responsePayload.items;
        const synchronizedItemsPayloads = this.getSynchronizedActionItemsPayloads(actionItems, operations, operationIdsState);
        const resultActions = [
          bulkActionsActions.cleanOperationIds(),
          ...synchronizedItemsPayloads.map(
            (synchronizedItemsPayload) => bulkActionsActions.changeBulkActionStatus({
              actionKey: synchronizedItemsPayload.actionKey,
              items: synchronizedItemsPayload.items
            })
          ),
          bulkActionsActions.perform(payload)
        ];
        return of(...resultActions);
      })
    );
  }
  getOperations(operationIdList) {
    const filter2 = `id=${operationIdList.join(",")}`;
    const fields = ["id", "status", "items.id", "items.status", "items.message", "entity_type"];
    return this._bulkOperationService.get({
      type: this._bulkOperationEntityType,
      filter: filter2,
      fields
    });
  }
  getSynchronizedActionItemsPayloads(actionItems, operations, operationIdsState) {
    return Object.entries(operationIdsState).map(([actionKey, operationId]) => {
      const operation = operations.find((o) => o.id === operationId);
      return {
        items: actionItems[actionKey].map((item) => ({
          actionKey: item.actionKey,
          itemId: item.itemId,
          status: operation?.items?.find((i) => i.id === item.itemId)?.status ?? item.status
        })),
        actionKey
      };
    });
  }
}
export {
  ActionBulkEpicsBuilder
};
//# sourceMappingURL=epics.js.map
